import { Loader2, ImagePlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { uploadImage } from "@/lib/api";
import { useSettingsForm } from "@/hooks/useSettingsForm";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ResourceManager } from "@/components/admin/ResourceManager";

export const AdminAbout = () => {
  const { form, update, save, saving } = useSettingsForm();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      update("about_image", url);
    } catch (err) {
      toast.error("Gagal mengunggah gambar.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-12">
      <div>
        <h1 className="font-display text-3xl text-ink tracking-tight mb-1">Tentang Kami</h1>
        <p className="text-stone2 text-sm mb-6">
          Edit teks dan foto pada bagian "Tentang Kami" di halaman utama.
        </p>

        <div className="rounded-2xl bg-white border border-border/60 p-6 space-y-5 max-w-2xl">
          <div>
            <Label className="mb-2 block">Judul</Label>
            <Input value={form.about_heading} onChange={(e) => update("about_heading", e.target.value)} />
          </div>
          <div>
            <Label className="mb-2 block">Bio Singkat (dipakai juga di sidebar Artikel)</Label>
            <Textarea rows={2} value={form.about_intro} onChange={(e) => update("about_intro", e.target.value)} />
          </div>
          <div>
            <Label className="mb-2 block">Riwayat Usaha (pisahkan paragraf dengan baris kosong)</Label>
            <Textarea
              rows={12}
              value={form.about_history}
              onChange={(e) => update("about_history", e.target.value)}
            />
          </div>
          <div>
            <Label className="mb-2 block">Foto</Label>
            <div className="flex items-center gap-3">
              {form.about_image && (
                <img src={form.about_image} alt="" className="h-16 w-16 rounded-lg object-cover border border-border/60" />
              )}
              <Input
                value={form.about_image}
                onChange={(e) => update("about_image", e.target.value)}
                placeholder="URL gambar, atau unggah file"
                className="flex-1"
              />
              <label className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-input cursor-pointer hover:bg-accent shrink-0">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
              </label>
            </div>
          </div>

          {form.about_image && (
            <div>
              <Label className="mb-2 block">Posisi Foto (geser sampai wajah kelihatan pas)</Label>
              <div className="flex gap-6 items-start">
                <div className="w-32 h-40 shrink-0 overflow-hidden rounded-2xl border border-border/60 bg-cream-200">
                  <img
                    src={form.about_image}
                    alt="Preview posisi foto"
                    className="h-full w-full object-cover"
                    style={{ objectPosition: `center ${form.about_image_position ?? 50}%` }}
                  />
                </div>
                <div className="flex-1 pt-4">
                  <Slider
                    value={[form.about_image_position ?? 50]}
                    onValueChange={([v]) => update("about_image_position", v)}
                    min={0}
                    max={100}
                    step={1}
                  />
                  <div className="flex justify-between text-xs text-stone2 mt-2">
                    <span>Atas</span>
                    <span>Tengah</span>
                    <span>Bawah</span>
                  </div>
                  <p className="text-stone2 text-xs mt-3">
                    Kotak di kiri adalah preview persis seperti tampilan di halaman Tentang Kami.
                  </p>
                </div>
              </div>
            </div>
          )}
          <Button
            onClick={save}
            disabled={saving}
            data-testid="about-save-btn"
            className="rounded-full bg-honey hover:bg-honey-dark text-cream font-semibold"
          >
            {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </div>
      </div>

      <ResourceManager
        title="Poin Manifesto"
        description="Tiga (atau lebih) poin bernomor di bagian Tentang Kami."
        queryKey="manifesto"
        endpoint="/manifesto"
        columns={[
          { key: "no", label: "No." },
          { key: "title", label: "Judul" },
        ]}
        fields={[
          { name: "no", label: "Nomor (mis. 01)", type: "text" },
          { name: "title", label: "Judul", type: "text" },
          { name: "desc", label: "Deskripsi", type: "textarea" },
          { name: "position", label: "Urutan Tampil", type: "number" },
        ]}
      />
    </div>
  );
};
